import { type HTMLAttributes, memo, type ReactNode } from 'react';

import { ProgressCircleIndicator } from '@/shared/components/progressCircleIndicator';
import { cn } from '@/shared/utils/cn';
import {
  getRarityFromProgress,
  rarityFitLabels,
  rarityTextColors,
} from '@/shared/utils/rarity';

export interface BaseProfileDetailsSectionHeaderProps extends HTMLAttributes<HTMLDivElement> {
  icon: ReactNode;
  label: ReactNode;
}

export interface ProfileDetailsSectionHeaderWithDataProps extends BaseProfileDetailsSectionHeaderProps {
  hasData: true;
  progress: number;
  subTitle?: ReactNode;
}

export interface ProfileDetailsSectionHeaderWithoutDataProps extends BaseProfileDetailsSectionHeaderProps {
  hasData?: false;
  progress?: unknown;
}

export type ProfileDetailsSectionHeaderProps =
  | ProfileDetailsSectionHeaderWithoutDataProps
  | ProfileDetailsSectionHeaderWithDataProps;

export const ProfileDetailsSectionHeader = memo(
  function ProfileDetailsSectionHeader(
    props: ProfileDetailsSectionHeaderProps,
  ) {
    const { className, hasData, icon, progress, label, ...rest } = props;

    if (!hasData) {
      return (
        <div {...rest} className={cn('flex items-center gap-3', className)}>
          {icon}
          <p className="font-nunito leading-8">
            <span className="text-cold-100 text-base font-black">{label}</span>{' '}
            <span className="text-cold-500 text-xs font-light">
              (last 90 days)
            </span>
          </p>
        </div>
      );
    }

    const progressInPercents = Math.round(progress * 100);
    const rarity = getRarityFromProgress(progressInPercents) ?? 'COMMON';
    const textColor = rarityTextColors[rarity];
    const subTitle = props.subTitle ?? '(last 90 days)';

    return (
      <div {...rest} className={cn('flex items-center gap-3', className)}>
        <ProgressCircleIndicator progress={progressInPercents} icon={icon} />
        <div className="flex flex-col">
          <p className="font-nunito leading-8">
            <span className="text-cold-100 text-base font-black">{label}</span>{' '}
            <span className="text-cold-500 text-xs font-light">{subTitle}</span>
          </p>
          <p className={cn('text-sm leading-[1] font-medium', textColor)}>
            {rarityFitLabels[rarity]} {progressInPercents}%
          </p>
        </div>
      </div>
    );
  },
);
